/**
 * A styled react-select wrapper
 *
 * to style the component, you may pass a `styles` prop. This styles prop is a function
 * that accepts a theme object as its single argument. It must return an object that
 * matches up with the react select style spec: https://react-select.com/styles
 */
import React, { forwardRef, FunctionComponent, useContext } from 'react';
import styled, { ThemeContext } from 'styled-components';
import Select, { components as reactSelectComponents } from 'react-select';
import AsyncSelect from 'react-select/async';
import CreatableSelect from 'react-select/creatable';
import { Box, Flex, Text } from 'rebass/styled-components';
import { SelectType } from '../enums';
import { ReactComponent as Searchglass } from '../assets/searchglass.svg';
import { ReactComponent as SelectCaretComponent } from '../assets/select-caret.svg';
import { scrollBG, scrollThumbBG } from './ScrollBox';

export const SearchglassPlaceholder: FunctionComponent<any> = (props: any) => (
  <reactSelectComponents.DropdownIndicator {...props}>
    <Flex flexWrap="nowrap" alignItems="center">
      <Box ml={2}>
        <Searchglass />
      </Box>
      <Text variant="placeholder" ml={2}>Type to search</Text>
    </Flex>
  </reactSelectComponents.DropdownIndicator>
);

export const SelectCaret: FunctionComponent<any> = () => (
  <Flex mr={2} ml={2}>
    <SelectCaretComponent />
  </Flex>
);

const StyledSelect: FunctionComponent<any> = forwardRef(({
  components = {},
  styles = () => ({}),
  type = SelectType.NORMAL,
  controlHeight = 32,
  isErrored,
  ...rest
}: any, ref) => {
  const themeContext: any = useContext(ThemeContext);

  const customComponents = {
    DropdownIndicator: SelectCaret,
    ...components,
  };

  const customStyles = {
    container: (provided: any) => ({
      ...provided,
      border: isErrored ? `1px solid ${themeContext.mode === 'dark' ? themeContext.colors.redLight : themeContext.colors.red}` : '1px solid transparent',
      borderRadius: '5px',
    }),
    clearIndicator: () => ({
      display: 'none',
    }),
    indicatorSeparator: () => ({
      display: 'none',
    }),
    menu: (provided: any) => ({
      ...provided,
      backgroundColor: themeContext.mode === 'light' ? themeContext.colors.whiteGrey : '#2C3249',
      color: themeContext.mode === 'light' ? themeContext.colors.deepGrey : '#ffffff',
      border: '1px solid rgba(255, 255, 255, 0.22)',
      boxShadow: '1px 1px 3px rgba(0, 0, 0, 0.15), 0px 1px 4px rgba(0, 0, 0, 0.17)',
    }),
    menuList: (provided: any) => ({
      ...provided,
      '::-webkit-scrollbar': {
        width: '8px',
      },
      '::-webkit-scrollbar-track': {
        background: 'rgba(255, 255, 255, 0.02)',
      },
      '::-webkit-scrollbar-thumb': {
        backgroundColor: themeContext.mode === 'light' ? themeContext.colors.greyLight : themeContext.colors.paynesGrey,
        outline: 'none',
        borderRadius: '8px',
      },
      '::-webkit-scrollbar-thumb:hover': {
        background: themeContext.mode === 'light' ? themeContext.colors.auroMetalSaurus : themeContext.colors.auroMetalSaurus,
      },
    }),
    option: (provided: any, state: any) => {
      const output = {
        ...provided,
        color: themeContext.mode === 'light' ? themeContext.colors.deepGrey : themeContext.colors.gretyLighter,
        fontSize: '13px',
        paddingTop: '8px',
        paddingBottom: '8px',
        paddingLeft: '8px',
      };

      if (state.isSelected && themeContext.mode === 'dark') {
        output.backgroundColor = 'rgba(255,255,255,0.3)';
      }

      return output;
    },
    control: (provided: any, state: any) => {
      const opts = {
        ...provided,
        boxShadow: 'none',
        background: themeContext.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : '#ffffff',
      };

      if (state.isFocused) {
        opts.border = themeContext.mode === 'dark' ? `1px solid ${themeContext.colors.brightCerulean}` : `1px solid ${themeContext.colors.periwinkle}`;
      } else {
        opts.border = themeContext.mode === 'dark' ? '1px solid rgba(255, 255, 255, 0.22)' : `1px solid ${themeContext.colors.greyLight}`;
      }

      return opts;
    },
    placeholder: (provided: any, state: any) => ({
      ...provided,
      display: state.isFocused ? 'none' : '',
      color: themeContext.mode === 'dark' ? themeContext.colors.whiteGrey : themeContext.colors.deepGrey,
      fontSize: '13px',
      fontStyle: 'italic',
      opacity: 0.4,
    }),
    singleValue: (provided: any, state: any) => ({
      ...provided,
      fontSize: '13px',
      fontWeight: 400,
      color: themeContext.mode === 'dark' ? '#FCFCFD' : 'hsl(0,0%,20%)',
    }),
    input: (provided: any, state: any) => ({
      ...provided,
      fontSize: '13px',
      marginLeft: '8px',
    }),
    indicatorsContainer: (provided: any, state: any) => ({
      ...provided,
      color: themeContext.mode === 'dark' ? '#FCFCFD' : 'hsl(0,0%,20%)',
    }),
    valueContainer: (provided: any) => ({
      ...provided,
      paddingLeft: '8px',
    }),
    ...styles(themeContext),
  };

  const selectTheme = (st: any) => (themeContext.mode === 'light' ? {
    ...st,
    borderRadius: 4,
    spacing: {
      ...st.spacing,
      controlHeight,
      baseUnit: 2,
    },
    colors: {
      ...st.colors,
      primary: 'hsl(0, 0%, 70%)',
      primary75: 'hsl(0, 0%, 75%)',
      primary50: 'hsl(0, 0%, 50%)',
      primary25: 'rgba(0, 0, 0, 0.08)',
    },
  } : {
    ...st,
    borderRadius: 4,
    spacing: {
      ...st.spacing,
      controlHeight,
      baseUnit: 2,
    },
    colors: {
      ...st.colors,
      dangerLight: themeContext.colors.greyLighter2,
      neutral0: 'transparent',
      neutral5: 'hsl(0, 0%, 95%)',
      neutral10: 'hsl(0, 0%, 10%)',
      neutral20: 'hsl(0, 0%, 20%)',
      neutral30: 'hsl(0, 0%, 30%)',
      neutral40: 'hsl(0, 0%, 40%)',
      neutral50: 'hsl(0, 0%, 50%)',
      neutral60: 'hsl(0, 0%, 60%)',
      neutral70: 'hsl(0, 0%, 70%)',
      neutral80: 'hsl(0, 0%, 80%)',
      primary: 'hsl(0, 0%, 95%)',
      primary75: 'hsl(0, 0%, 70%)',
      primary50: 'hsl(0, 0%, 50%)',
      primary25: 'rgba(255, 255, 255, 0.08)',
    },
  });

  if (type === SelectType.CREATABLE) {
    return (
      <CreatableSelect
        {...rest}
        ref={ref}
        styles={customStyles}
        theme={selectTheme}
        components={customComponents}
        classNamePrefix="lp-select"
      />
    );
  }

  if (type === SelectType.ASYNC) {
    return (
      <AsyncSelect
        {...rest}
        ref={ref}
        styles={customStyles}
        theme={selectTheme}
        components={customComponents}
        classNamePrefix="lp-select"
      />
    );
  }

  return (
    <Select
      {...rest}
      ref={ref}
      styles={customStyles}
      theme={selectTheme}
      components={customComponents}
      classNamePrefix="lp-select"
    />
  );
});

export const ExtraStyledSelect = styled(StyledSelect)`
  &::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.02);
  }
  &::-webkit-scrollbar {
    width: 8px;
  }
  &::-webkit-scrollbar-thumb {
    background-color: ${scrollThumbBG};
    outline: none;
    border-radius: 8px;

    &:hover {
      background-color: ${scrollBG};
    }
  }
`;

export default ExtraStyledSelect;
